import { AccountInfo, PublicClientApplication } from '@azure/msal-browser';
import { getEntraConfig } from './config';

let msalInstance: PublicClientApplication | null = null;
let initialized = false;
let configKey = '';

const loginScopes = ['openid', 'profile', 'email'];

const buildConfigKey = () => {
  const entraConfig = getEntraConfig();
  return [
    entraConfig.clientId,
    entraConfig.authority,
    entraConfig.redirectUri,
  ].join('|');
};

const getMsalInstance = async (requireConfig = true) => {
  const entraConfig = getEntraConfig();
  if (!entraConfig.tenantId || !entraConfig.clientId) {
    if (requireConfig) {
      throw new Error('Microsoft Entra sign-in is not configured');
    }
    return null;
  }

  const nextConfigKey = buildConfigKey();
  if (!msalInstance || configKey !== nextConfigKey) {
    msalInstance = new PublicClientApplication({
      auth: {
        clientId: entraConfig.clientId,
        authority: entraConfig.authority,
        redirectUri: entraConfig.redirectUri,
      },
      cache: {
        cacheLocation: 'localStorage',
      },
    });
    initialized = false;
    configKey = nextConfigKey;
  }

  if (!initialized) {
    await msalInstance.initialize();
    initialized = true;
  }

  return msalInstance;
};

const getAccount = (instance: PublicClientApplication, redirectAccount?: AccountInfo | null) => {
  const activeAccount = instance.getActiveAccount();
  if (activeAccount) {
    return activeAccount;
  }

  if (redirectAccount) {
    instance.setActiveAccount(redirectAccount);
    return redirectAccount;
  }

  const [cachedAccount] = instance.getAllAccounts();
  if (cachedAccount) {
    instance.setActiveAccount(cachedAccount);
    return cachedAccount;
  }

  return null;
};

const acquireTokenSilently = async (
  instance: PublicClientApplication,
  account: AccountInfo,
  forceRefresh = false
) => {
  const result = await instance.acquireTokenSilent({
    account,
    scopes: loginScopes,
    ...(forceRefresh ? { forceRefresh: true } : {}),
  });
  return result.idToken || null;
};

export const completeMicrosoftSignIn = async () => {
  const instance = await getMsalInstance(false);
  if (!instance) {
    return null;
  }

  const result = await instance.handleRedirectPromise();
  if (!result) {
    return null;
  }

  const account = getAccount(instance, result.account);
  return result.idToken || (account ? acquireTokenSilently(instance, account) : null);
};

export const restoreMicrosoftSignIn = async () => {
  const instance = await getMsalInstance(false);
  if (!instance) {
    return null;
  }

  const account = getAccount(instance);
  if (!account) {
    return null;
  }

  try {
    return await acquireTokenSilently(instance, account);
  } catch {
    return null;
  }
};

export const refreshMicrosoftSignIn = async () => {
  const instance = await getMsalInstance(false);
  if (!instance) {
    return null;
  }

  const account = getAccount(instance);
  if (!account) {
    return null;
  }

  try {
    return await acquireTokenSilently(instance, account, true);
  } catch {
    return null;
  }
};

export const signInWithMicrosoft = async () => {
  const instance = await getMsalInstance();
  if (!instance) {
    throw new Error('Microsoft Entra sign-in is not configured');
  }

  await instance.loginRedirect({
    scopes: loginScopes,
  });
};
